import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { track } from '@/lib/analytics';
import { Check, ArrowRight, Sprout, MapPin, Bell, Sparkles } from 'lucide-react';

/**
 * Landing "Agricoltori" — targeting agricoltori, imprenditori agricoli, coop
 * che cercano terreno da coltivare/affittare. Tono operativo, focus sul matching.
 */
export default function Agricoltori() {
  useEffect(() => {
    track('landing_view', { audience: 'agricoltori' });
    document.title = 'Trova il terreno perfetto per la tua attività agricola — AgriNexus';
    const set = (sel, attr, val) => document.querySelector(sel)?.setAttribute(attr, val);
    set('meta[name="description"]', 'content', 'Cerchi terreno da affittare o comprare per la tua attività agricola? Su AgriNexus impostà i tuoi criteri e ricevi i terreni che matchano le tue esigenze. Gratis, in tutta Italia.');
  }, []);

  const onCTA = (name) => track('landing_cta', { audience: 'agricoltori', cta: name });

  return (
    <div className="min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?auto=format&fit=crop&w=1600&q=80" alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-foreground/70"/>
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-24 md:py-32 text-background">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-accent mb-5">Per agricoltori e imprenditori agricoli</div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95] max-w-3xl">
            Trova il terreno<br/><span className="italic text-accent">che ti manca.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-background/80 max-w-2xl">
            Imposta i tuoi criteri (superficie, coltura, budget, regione) e AgriNexus ti mostra solo terreni che matchano. Con analisi AI, foto reali e contatto diretto col proprietario.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/esplora" onClick={() => onCTA('explore_hero')} data-testid="landing-cta-explore" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-accent text-foreground font-medium hover:opacity-90 transition">
              Cerca terreni ora <ArrowRight size={16}/>
            </Link>
            <Link to="/preferenze" onClick={() => onCTA('prefs_hero')} className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-background/30 text-background/90 hover:bg-background/10 transition">
              Imposta i tuoi criteri
            </Link>
          </div>
        </div>
      </section>

      {/* MATCHING */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-terracotta">Matching intelligente</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter mt-4">Basta scorrere annunci a caso.</h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Compila il wizard preferenze una volta e vedi <b>solo</b> i terreni che matchano davvero: superficie giusta, coltura compatibile, prezzo nel tuo budget, entro il raggio che scegli.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { I: Sprout, t: 'Colture compatibili', d: 'Indica vite, olivo, ortaggi, foraggio… il sistema filtra solo terreni adatti.' },
                { I: MapPin, t: 'Raggio geografico', d: 'Dai il tuo punto di partenza e il raggio (10, 30, 100 km). Zero terreni fuori zona.' },
                { I: Bell, t: 'Alert automatici', d: 'Salva la ricerca: ricevi email quando appare un terreno nuovo che matcha.' },
                { I: Sparkles, t: 'Report AI istantaneo', d: 'Ogni terreno ha già l\'analisi di clima, altitudine, potenziale colturale.' },
              ].map(({I,t,d})=>(
                <div key={t} className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                    <I size={18} className="text-terracotta"/>
                  </div>
                  <div>
                    <div className="font-medium">{t}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">{d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=1200&q=80" alt="" className="rounded-2xl w-full h-[560px] object-cover shadow-xl"/>
            <div className="absolute -top-4 -right-4 rounded-full bg-foreground text-background px-5 py-3 shadow-xl">
              <div className="text-xs uppercase tracking-widest">Match score</div>
              <div className="font-display text-3xl mt-1">92<span className="text-lg">/100</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* PARTITE UPSELL */}
      <section className="bg-secondary/40 py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-terracotta">Anche il tuo raccolto</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter mt-4">Vendi le tue partite <span className="italic">senza intermediari.</span></h2>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-lg">
              Su AgriNexus non trovi solo terreni: c'è un marketplace dedicato alle <b>partite di frutti, ortaggi e cereali</b>. Vendi direttamente a ristoratori, grossisti, cooperative. Zero commissioni.
            </p>
            <Link to="/partite" onClick={() => onCTA('batches_cta')} className="mt-8 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-foreground text-background font-medium hover:opacity-90 transition">
              Vai al marketplace partite <ArrowRight size={16}/>
            </Link>
          </div>
          <img src="https://images.unsplash.com/photo-1610348725531-843dff563e2c?auto=format&fit=crop&w=1200&q=80" alt="" className="rounded-2xl w-full h-[380px] object-cover shadow-xl"/>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
        <h2 className="font-display text-4xl md:text-6xl tracking-tighter">
          <span className="italic">Coltiva</span> qualcosa di grande.
        </h2>
        <p className="mt-6 text-muted-foreground max-w-xl mx-auto">Registrazione gratuita, nessuna carta richiesta. Configura le tue preferenze e AgriNexus fa il resto.</p>
        <Link to="/registrati" onClick={() => onCTA('signup_footer')} className="mt-10 inline-flex items-center gap-2 h-14 px-9 rounded-full bg-foreground text-background text-base font-medium hover:opacity-90 transition">
          Crea account gratuito <ArrowRight size={18}/>
        </Link>
      </section>
    </div>
  );
}
